// ISSUE-987. Pure grouping for the Lineup tab: which players are starters
// for the shown team, and how the roster's provider groups fold around
// them. No React here.
export const STARTERS_GROUP_KEY = "starters";
export const STARTERS_GROUP_LABEL = "Starters";

export type typeLineupPlayer = {
  playerId: string;
  name: string;
  jerseyNumber: string | null;
  position: string | null;
  /** The stored headshot's media URL, or null for the silhouette (ISSUE-983). */
  headshotUrl: string | null;
};

/**
 * ISSUE-1086. The roster groups the game detail Rosters tab shows: every
 * group except the vendor's injured group (sports-api marks it
 * isInjuredGroup). Those players are listed on the Injuries tab instead.
 * Practice Squad and every other group stay. Never mutates input.
 */
export function filterRosterGroups<
  typeGroup extends { isInjuredGroup?: boolean },
>(groups: readonly typeGroup[]): typeGroup[] {
  return groups.filter((group) => group.isInjuredGroup !== true);
}

export type typeLineupSection = {
  groupKey: string;
  label: string;
  players: typeLineupPlayer[];
};

/** The ids of the shown team's starters, per the event's stored lineup. The
 *  home side is checked first when a lineup happens to name a team on both
 *  sides (it never should, but the check is stated rather than assumed). A
 *  null lineup or no side matching teamId yields an empty set. */
export function resolveStarterIds(input: {
  lineup: {
    home: {
      teamId: string;
      players: readonly { playerId: string; isStarter: boolean }[];
    };
    away: {
      teamId: string;
      players: readonly { playerId: string; isStarter: boolean }[];
    };
  } | null;
  teamId: string;
}): Set<string> {
  if (input.lineup === null) {
    return new Set();
  }
  const side =
    input.lineup.home.teamId === input.teamId
      ? input.lineup.home
      : input.lineup.away.teamId === input.teamId
        ? input.lineup.away
        : null;
  if (side === null) {
    return new Set();
  }
  const ids = new Set<string>();
  for (const player of side.players) {
    if (player.isStarter) {
      ids.add(player.playerId);
    }
  }
  return ids;
}

/**
 * Folds the roster's provider groups around the known starters. With no
 * starters known, the provider's own groups and order are kept as given
 * (empty groups dropped). With starters known, a Starters section leads,
 * built from the roster in group order then player order, and each input
 * group appears afterward minus whichever of its players are starters
 * (empty groups dropped). Never mutates input.
 */
export function buildLineupSections(input: {
  groups: readonly {
    groupKey: string;
    label: string;
    players: readonly {
      playerId: string;
      name: string;
      jerseyNumber?: string | null;
      position?: string | null;
      headshotUrl?: string | null;
    }[];
  }[];
  starterPlayerIds: ReadonlySet<string>;
}): typeLineupSection[] {
  const normalize = (player: {
    playerId: string;
    name: string;
    jerseyNumber?: string | null;
    position?: string | null;
    headshotUrl?: string | null;
  }): typeLineupPlayer => ({
    playerId: player.playerId,
    name: player.name,
    jerseyNumber: player.jerseyNumber ?? null,
    position: player.position ?? null,
    headshotUrl: player.headshotUrl ?? null,
  });

  if (input.starterPlayerIds.size === 0) {
    return input.groups
      .map((group) => ({
        groupKey: group.groupKey,
        label: group.label,
        players: group.players.map(normalize),
      }))
      .filter((section) => section.players.length > 0);
  }

  const starters: typeLineupPlayer[] = [];
  const rest: typeLineupSection[] = [];
  for (const group of input.groups) {
    const remaining: typeLineupPlayer[] = [];
    for (const player of group.players) {
      if (input.starterPlayerIds.has(player.playerId)) {
        starters.push(normalize(player));
      } else {
        remaining.push(normalize(player));
      }
    }
    if (remaining.length > 0) {
      rest.push({
        groupKey: group.groupKey,
        label: group.label,
        players: remaining,
      });
    }
  }

  const sections: typeLineupSection[] = [];
  if (starters.length > 0) {
    sections.push({
      groupKey: STARTERS_GROUP_KEY,
      label: STARTERS_GROUP_LABEL,
      players: starters,
    });
  }
  sections.push(...rest);
  return sections;
}
